import { Category, CategoryEntity, GraphQLResponse } from "../../Components/processors";
import { GET_ALL_CATEGORIES_FOR_LOCATIONS_PARAMS } from "../../Components/graphql";
import { ContextType } from "../misc/types";
import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { finaliseCategories } from "../categoryDisplayControl";
import { EMPTY_ARRAY, SEPARATOR, SEWERAGE_SERVICES } from "../misc/constants";
import _ from "lodash";

const buildTree = (data: Category[]) => {
    const adminIdToCategory: Record<string, Category> = _.keyBy(data ?? [], d => d?.adminId);
    const tIdToCategory: Record<number, Category> = _.keyBy(data ?? [], d => d?.tid);

    // populate child ids
    (data ?? []).forEach(d => {
        if (d?.parentId != null && tIdToCategory?.[d?.parentId] == null) {
            d.parentId = null;
        } else if (d?.parentId != null && tIdToCategory?.[d?.parentId] != null) {
            const parent = tIdToCategory?.[d?.parentId];
            parent.childIds = _.uniq([...(parent.childIds ?? []), d.tid]);
        }
    });

    return { data, adminIdToCategory, tIdToCategory };
};

const buildTheme = (roots: Category[], idToCat: Record<number, Category>, theme: Record<number, string>) => {
    if (!roots.length) return theme;

    roots.forEach(root => {
        const parent = idToCat?.[root.parentId];
        if (
            parent?.theme != null &&
            parent?.theme?.toString()?.trim() !== "" &&
            (root?.theme == null || root?.theme?.toString().trim() === "")
        ) {
            theme[root.tid] = parent.theme;
        }
    });

    const children =
        _.flattenDeep(roots?.map(r => r.childIds ?? []))
            ?.map(id => idToCat?.[id])
            ?.filter(d => d != null) ?? [];

    return buildTheme(children, idToCat, theme);
};

const categoryEntitiesToCategories = (data?: GraphQLResponse<CategoryEntity[]>, contextType?: ContextType) => {
    let _data: Category[] = undefined;

    if (!data?.result?.length) {
        _data = [];
    }

    if (data?.result?.length) {
        _data = data?.result?.map(
            ({
                parentId,
                graphContent,
                tableContent,
                infoContent,

                comparisonGraphContent,
                comparisonTableContent,
                comparisonInfoContent,

                ...rest
            }) => {
                const isSewerageServices = rest?.adminId?.indexOf(SEWERAGE_SERVICES) >= 0 ? true : false;
                const _graphContent = graphContent?.filter(d => d != null) ?? [];
                const _tableContent = tableContent?.filter(d => d != null) ?? [];
                const _infoContent = infoContent?.filter(d => d != null) ?? [];

                const _comparisonGraphContent = comparisonGraphContent?.filter(d => d != null) ?? [];
                const _comparisonTableContent = comparisonTableContent?.filter(d => d != null) ?? [];
                const _comparisonInfoContent = comparisonInfoContent?.filter(d => d != null) ?? [];

                return {
                    ...rest,
                    isSewerageServices,
                    parentId,
                    childIds: [],

                    graphContent: _graphContent,
                    tableContent: _tableContent,
                    infoContent: _infoContent,

                    comparisonGraphContent: _comparisonGraphContent,
                    comparisonTableContent: _comparisonTableContent,
                    comparisonInfoContent: _comparisonInfoContent,

                    catGraphContent:
                        contextType === "comparison"
                            ? _comparisonGraphContent
                            : contextType === "info"
                            ? _graphContent
                            : [],

                    catTableContent:
                        contextType === "comparison"
                            ? _comparisonTableContent
                            : contextType === "info"
                            ? _tableContent ?? []
                            : [],

                    catInfoContent:
                        contextType === "comparison"
                            ? _comparisonInfoContent
                            : contextType === "info"
                            ? _infoContent
                            : []
                };
            }
        );
    }

    return _data;
};

const defaultCategoryMeta = {
    loading: true,
    error: undefined,
    categories: undefined,
    roots: undefined,
    adminIdToCategory: undefined,
    tIdToCategory: undefined
};

export const useCategory = (locationAdminIds?: string[], contextType?: ContextType) => {
    const [categoryMeta, setCategoryMeta] = useState(defaultCategoryMeta);

    const _locationAdminIds = useMemo(
        () => {
            return locationAdminIds ?? (EMPTY_ARRAY as Array<string>);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [_.join(locationAdminIds ?? EMPTY_ARRAY, SEPARATOR)]
    );

    // retrieve all categories to collect its hierachy
    // const { query: categoriesQuery, options: categoriesOptions } = GET_ALL_CATEGORIES_PARAMS();
    // const {
    //     loading: categoriesLoading,
    //     data: categories,
    //     error: categoriesError
    // } = useQuery<GraphQLQuery<CategoryEntity[]>>(categoriesQuery, categoriesOptions);

    // retrieve all categories where location ids can exist

    const first = _locationAdminIds[0];
    const second = _locationAdminIds[1];

    // split the query to leverage caches if provider info has been pre-rendered
    // it also assumes that a category to display, it needs to have
    //  + a global rendering ge
    //  + a specific provider rendering ge
    const { query: categoriesForLocationsFirstQuery, options: categoriesForLocationsFirstOptions } =
        GET_ALL_CATEGORIES_FOR_LOCATIONS_PARAMS([first].filter(d => d != null && d.trim() !== ""));

    const { query: categoriesForLocationsSecondQuery, options: categoriesForLocationsSecondOptions } =
        GET_ALL_CATEGORIES_FOR_LOCATIONS_PARAMS([second].filter(d => d != null && d.trim() !== ""));

    // console.log(
    //     categoriesForLocationsFirstQuery,
    //     categoriesForLocationsFirstOptions,
    //     [first].filter(d => d != null && d.trim() !== ""),
    //     [second].filter(d => d != null && d.trim() !== "")
    // );
    const {
        loading: categoriesForLocationsLoadingFirst,
        data: categoriesForLocationsFirst,
        error: categoriesForLocationsErrorFirst
    } = useQuery<GraphQLResponse<CategoryEntity[]>>(
        categoriesForLocationsFirstQuery,
        categoriesForLocationsFirstOptions
    );

    const {
        loading: categoriesForLocationsLoadingSecond,
        data: categoriesForLocationsSecond,
        error: categoriesForLocationsErrorSecond
    } = useQuery<GraphQLResponse<CategoryEntity[]>>(
        categoriesForLocationsSecondQuery,
        categoriesForLocationsSecondOptions
    );

    // console.log(
    //     categoriesForLocationsFirst,
    //     categoriesForLocationsLoadingFirst,
    //     categoriesForLocationsSecond,
    //     categoriesForLocationsLoadingSecond
    // );
    const {
        loading: categoriesForLocationsLoading,
        data: categoriesForLocations,
        error: categoriesForLocationsError
    } = useMemo(() => {
        const loading = categoriesForLocationsLoadingFirst || categoriesForLocationsLoadingSecond;
        const error = categoriesForLocationsErrorFirst || categoriesForLocationsErrorSecond;

        const entities = [
            ...(categoriesForLocationsFirst?.result ?? (EMPTY_ARRAY as CategoryEntity[])),
            ...(categoriesForLocationsSecond?.result ?? (EMPTY_ARRAY as CategoryEntity[]))
        ];

        const unqEntities = _.uniqBy(entities, d => d.tid);
        const data: GraphQLResponse<CategoryEntity[]> = {
            result: unqEntities
        };

        return {
            error,
            loading,
            data
        };
    }, [
        categoriesForLocationsLoadingFirst,
        categoriesForLocationsLoadingSecond,
        categoriesForLocationsErrorFirst,
        categoriesForLocationsErrorSecond,
        categoriesForLocationsFirst,
        categoriesForLocationsSecond
    ]);

    // based on context type to filter out the results
    // const loading = useMemo(() => {
    //     return categoriesLoading || categoriesForLocationsLoading;
    // }, [categoriesLoading, categoriesForLocationsLoading]);

    // const error = useMemo(() => {
    //     return categoriesError || categoriesForLocationsError;
    // }, [categoriesError, categoriesForLocationsError]);

    useEffect(() => {
        const load = async () => {
            const loading = categoriesForLocationsLoading;
            const error = categoriesForLocationsError;

            if (loading) {
                setCategoryMeta(state => {
                    return {
                        ...state,
                        loading
                    };
                });
                return;
            }

            if (error) {
                console.log(error);
                return;
            }

            if (_locationAdminIds.length < 1) return;

            // filter by context type
            // catGraphContent might have values but not for this given pair
            // (the selection was taken due to the valid of data in info display).
            // For example:
            // C is being queried
            // graphContent: ["C"]
            // catGraphContent: ["A", "B"]

            const adminIdToOne: Record<string, 1> = _locationAdminIds.reduce((prev, current) => {
                prev[current] = 1;
                return prev;
            }, {});

            let _categoriesForLocations = categoryEntitiesToCategories(categoriesForLocations, contextType);

            _categoriesForLocations?.forEach(category => {
                const { catGraphContent } = category;
                const newCatGraphContent = catGraphContent.filter(
                    ge =>
                        // global
                        (ge.displayFor === "all" && (!ge?.providers || ge?.providers?.length === 0)) ||
                        // has the provider, and display for node
                        (ge.displayFor === "node" &&
                            ge?.providers?.find(p => adminIdToOne[p?.adminId] === 1) != null) ||
                        // has the providers, and display for pair
                        (_locationAdminIds?.length > 1 &&
                            ge.displayFor === "pair" &&
                            _.uniq(
                                ge?.providers
                                    ?.map(p => p?.adminId)
                                    ?.filter(d => d != null && d.trim() !== "" && adminIdToOne[d] === 1) ?? []
                            ).length === _locationAdminIds.length)
                );

                category.catGraphContent = newCatGraphContent;
            });

            _categoriesForLocations = _categoriesForLocations.filter(d => d?.catGraphContent?.length > 0);

            // finalise categories based on the visibility of data
            const _tidToCategory = _.keyBy(_categoriesForLocations, d => d.tid);
            const _ftidToCategory = {};

            _categoriesForLocations = _.sortBy(_categoriesForLocations, d => d.weight);
            let panelCategories = _categoriesForLocations.filter(d => d.displayType === "panel");
            panelCategories = await finaliseCategories(_locationAdminIds, panelCategories);

            const traverseUpward = (
                categories: Category[],
                tidToCategory: Record<number, Category>,
                result: Record<number, Category>
            ) => {
                if (!categories || !categories.length) return;

                categories.forEach(category => {
                    result[category.tid] = category;
                });

                traverseUpward(
                    categories
                        ?.map(category => {
                            return tidToCategory?.[category?.parentId];
                        })
                        ?.filter(d => d != null) ?? [],
                    tidToCategory,
                    result
                );
            };
            traverseUpward(panelCategories, _tidToCategory, _ftidToCategory);
            _categoriesForLocations = _.sortBy(Object.values(_ftidToCategory), d => d.weight);

            // build category tree
            const { data: _dataWithTree, adminIdToCategory, tIdToCategory } = buildTree(_categoriesForLocations);
            _categoriesForLocations = _dataWithTree;

            // build category theme
            const roots = _categoriesForLocations.filter(d => !d?.parentId && d?.childIds?.length > 0);
            const tIdToTheme = buildTheme(roots, tIdToCategory, {});
            _categoriesForLocations.forEach(d => {
                if (tIdToTheme[d.tid] != null) d.theme = tIdToTheme[d.tid];
            });
            Object.entries(tIdToCategory).forEach(value => {
                const tid = value?.[1]?.tid;
                if (tid != null && tIdToTheme[tid] != null) value[1].theme = tIdToTheme[tid];
            });

            Object.entries(adminIdToCategory).forEach(value => {
                const tid = value?.[1]?.tid;
                if (tid != null && tIdToTheme[tid] != null) value[1].theme = tIdToTheme[tid];
            });

            setCategoryMeta({
                loading: categoriesForLocationsLoading,
                error: categoriesForLocationsError,
                categories: _categoriesForLocations,
                roots,
                adminIdToCategory,
                tIdToCategory
            });
        };
        load();
    }, [
        categoriesForLocationsLoading,
        categoriesForLocationsError,
        categoriesForLocations,
        _locationAdminIds,
        setCategoryMeta,
        contextType
    ]);

    return categoryMeta;
};
